<template>
  <!-- =============================================================================
       FORMULARIO DE PRESUPUESTO DE SEGURO
       -----------------------------------------------------------------------------
       El mismo formulario que cierra la home, extraido a un componente para poder
       reutilizarlo en las paginas del silo de seguros. Envia por fetch a Formspree,
       sin sacar al usuario de la web. Soporta castellano y catalan.
       ============================================================================= -->
  <!-- data-clarity-mask: si algun dia se activa Microsoft Clarity, todo lo que
       haya dentro de este bloque se graba tapado, aunque en el panel de Clarity
       alguien cambie el modo de enmascarado a Relaxed. Los datos personales que
       escriba el usuario no salen nunca en las grabaciones. -->
  <section class="gr-fs" :class="{ 'gr-fs--sin-margen': sinMargen }" data-clarity-mask="true">
    <div class="gr-fs-card">
      <component :is="tituloTag" class="gr-fs-titulo">{{ t.titulo }}</component>
      <p class="gr-fs-sub">{{ t.subtitulo }}</p>

      <v-form ref="formSeguro" v-model="seguroValido" @submit.prevent="enviarSeguro">
        <v-row dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="seguro.nombre"
              :label="t.nombre"
              outlined
              dense
              required
              :rules="[rulesSeguro.required]"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="seguro.telefono"
              :label="t.telefono"
              outlined
              dense
              required
              :rules="[rulesSeguro.required]"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="seguro.email"
              label="Email"
              type="email"
              outlined
              dense
              required
              :rules="[rulesSeguro.required, rulesSeguro.email]"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-select
              v-model="seguro.tipo"
              :items="t.tipos"
              :label="t.tipo"
              outlined
              dense
              required
              :rules="[rulesSeguro.required]"
            />
          </v-col>
          <v-col cols="12">
            <v-textarea
              v-model="seguro.mensaje"
              :label="t.mensaje"
              outlined
              dense
              rows="3"
              required
              :rules="[rulesSeguro.required]"
            />
          </v-col>
        </v-row>

        <!-- Campo trampa antispam: invisible para el usuario -->
        <input
          v-model="honeypot"
          class="gr-hp"
          type="text"
          name="_gotcha"
          tabindex="-1"
          autocomplete="off"
          aria-hidden="true"
        >

        <captcha-turnstile
          ref="captcha"
          :site-key="captchaSiteKey"
          :lang="lang"
          @token="onCaptchaToken"
        />

        <v-btn
          type="submit"
          large
          depressed
          class="gr-fs-btn"
          :loading="enviandoSeguro"
          :disabled="enviandoSeguro"
        >
          {{ t.boton }}
        </v-btn>

        <v-alert
          v-if="envioSeguro === 'ok'"
          dense
          text
          type="success"
          class="gr-fs-alert mt-4"
        >
          {{ t.ok }}
        </v-alert>

        <v-alert
          v-if="envioSeguro === 'captcha'"
          dense
          text
          type="info"
          class="gr-fs-alert mt-4"
        >
          {{ t.captcha }}
        </v-alert>

        <v-alert
          v-if="envioSeguro === 'error'"
          dense
          text
          type="error"
          class="gr-fs-alert mt-4"
        >
          {{ t.error }}
        </v-alert>
      </v-form>

      <div class="gr-fs-wa-alt">
        <span class="gr-fs-wa-text">{{ t.prefieres }}</span>
        <v-btn
          :href="enlaceWhatsapp"
          target="_blank"
          rel="noopener"
          depressed
          class="gr-fs-wa-btn"
        >
          <v-icon left>mdi-whatsapp</v-icon>
          {{ t.waBoton }}
        </v-btn>
      </div>
    </div>
  </section>
</template>

<script>
  const TEXTOS = {
    es: {
      titulo: 'Pídenos presupuesto de seguro',
      subtitulo: 'Sin compromiso. Sin letra pequeña.',
      nombre: 'Nombre',
      telefono: 'Teléfono',
      tipo: 'Tipo de seguro',
      mensaje: 'Cuéntanos qué necesitas',
      boton: 'Pedir presupuesto',
      ok: '¡Gracias! Hemos recibido tu solicitud y te responderemos lo antes posible.',
      error: 'No hemos podido enviar el formulario. Inténtalo de nuevo o escríbenos por WhatsApp.',
      captcha: 'Estamos comprobando que no eres un robot. Espera un segundo y vuelve a darle a enviar.',
      prefieres: '¿Prefieres escribirnos directamente?',
      waBoton: 'Seguros por WhatsApp',
      waMensaje: 'Hola, quiero información sobre un seguro de...',
      obligatorio: 'Campo obligatorio',
      emailInvalido: 'Email no válido',
      tipos: [
        'Vehículo (auto, moto, furgoneta)',
        'Camión o flota',
        'Hogar o comunidad',
        'Comercio, profesional o PYME',
        'Transporte de mercancías',
        'Salud, vida o accidentes',
        'Viajes o responsabilidad civil',
        'Otro'
      ]
    },

    ca: {
      titulo: "Demana'ns pressupost d'assegurança",
      subtitulo: 'Sense compromís. Sense lletra petita.',
      nombre: 'Nom',
      telefono: 'Telèfon',
      tipo: "Tipus d'assegurança",
      mensaje: "Explica'ns què necessites",
      boton: 'Demanar pressupost',
      ok: 'Gràcies! Hem rebut la teva sol·licitud i et respondrem al més aviat possible.',
      error: 'No hem pogut enviar el formulari. Torna-ho a provar o escriu-nos per WhatsApp.',
      captcha: 'Estem comprovant que no ets un robot. Espera un segon i torna a enviar.',
      prefieres: "Prefereixes escriure'ns directament?",
      waBoton: 'Assegurances per WhatsApp',
      waMensaje: 'Hola, vull informació sobre una assegurança de...',
      obligatorio: 'Camp obligatori',
      emailInvalido: 'Email no vàlid',
      tipos: [
        'Vehicle (cotxe, moto, furgoneta)',
        'Camió o flota',
        'Llar o comunitat',
        'Comerç, professional o PIME',
        'Transport de mercaderies',
        'Salut, vida o accidents',
        'Viatges o responsabilitat civil',
        'Altres'
      ]
    }
  };

  import captcha from '@/mixins/captcha';

  export default {
    name: 'FormularioSeguro',

    mixins: [captcha],

    props: {
      lang: { type: String, default: 'es' },
      whatsapp: { type: String, default: '34624217274' },
      /* De donde llega el formulario. Va en el asunto del correo para saber
         si el aviso viene de la home o de una pagina del silo de seguros. */
      origen: { type: String, default: 'web' },

      /* La home ya tiene un h2 para toda la sección de servicios: allí este
         título va como h3 para no romper la jerarquía de encabezados. */
      tituloTag: { type: String, default: 'h2' },

      /* La home coloca el formulario dentro de una fila que ya trae su propio
         margen superior. */
      sinMargen: { type: Boolean, default: false }
    },

    data() {
      return {
        seguro: { nombre: '', telefono: '', email: '', tipo: '', mensaje: '' },
        seguroValido: false,
        enviandoSeguro: false,
        envioSeguro: null
      };
    },

    computed: {
      t() {
        return TEXTOS[this.lang] || TEXTOS.es;
      },

      rulesSeguro() {
        const t = this.t;
        return {
          required: v => (!!v && !!String(v).trim()) || t.obligatorio,
          email: v => !v || /.+@.+\..+/.test(v) || t.emailInvalido
        };
      },

      enlaceWhatsapp() {
        return 'https://wa.me/' + this.whatsapp + '?text=' + encodeURIComponent(this.t.waMensaje);
      }
    },

    methods: {
      async enviarSeguro() {
        /* Campo trampa relleno: es un bot. Fingimos que ha ido bien y no
           mandamos nada, para no darle pistas. */
        if (this.esSpam()) {
          this.envioSeguro = 'ok';
          return;
        }

        if (!this.$refs.formSeguro.validate()) {
          this.envioSeguro = 'error';
          return;
        }

        if (this.captchaPendiente()) {
          this.envioSeguro = 'captcha';
          return;
        }

        this.enviandoSeguro = true;
        this.envioSeguro = null;

        try {
          const respuesta = await fetch('https://formspree.io/f/mlgqpqkw', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify(Object.assign({
              Nombre: this.seguro.nombre,
              'Teléfono': this.seguro.telefono,
              Email: this.seguro.email,
              'Tipo de seguro': this.seguro.tipo,
              Mensaje: this.seguro.mensaje,
              Origen: this.origen,
              _subject: 'Presupuesto de seguro (' + this.origen + ')'
            }, this.camposAntispam()))
          });

          if (respuesta.ok) {
            this.envioSeguro = 'ok';
            this.seguro = { nombre: '', telefono: '', email: '', tipo: '', mensaje: '' };
            this.$refs.formSeguro.resetValidation();
          } else {
            this.envioSeguro = 'error';
          }
        } catch (e) {
          this.envioSeguro = 'error';
        } finally {
          this.enviandoSeguro = false;
          this.reiniciarCaptcha();
        }
      }
    }
  };
</script>

<style scoped>
  .gr-fs {
    margin-top: 56px;
  }

  .gr-fs--sin-margen {
    margin-top: 0;
  }

  .gr-fs-card {
    background: #fff;
    border: 1px solid rgba(69, 78, 79, 0.16);
    border-radius: 14px;
    padding: 32px;
  }

  .gr-fs-titulo {
    font-size: 1.45rem;
    font-weight: 700;
    line-height: 1.25;
    color: #454E4F;
    margin: 0 0 4px;
  }

  .gr-fs-sub {
    font-size: 0.98rem;
    line-height: 1.6;
    color: rgba(37, 42, 43, 0.72);
    margin: 0 0 18px;
  }

  .gr-fs-btn.v-btn {
    background-color: #900A30 !important;
    color: #fff !important;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    border-radius: 8px;
    padding: 0 30px !important;
    margin-top: 8px;
  }

  .gr-fs-alert {
    font-size: 0.95rem;
  }

  .gr-fs-wa-alt {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid rgba(69, 78, 79, 0.16);
  }

  .gr-fs-wa-text {
    font-size: 0.95rem;
    color: rgba(37, 42, 43, 0.72);
  }

  .gr-fs-wa-btn.v-btn {
    background-color: #25D366 !important;
    color: #fff !important;
    font-weight: 700;
    text-transform: none;
    border-radius: 999px;
    height: auto !important;
    min-height: 48px;
    max-width: 100%;
    white-space: normal;
    padding: 10px 22px !important;
  }

  .gr-fs-wa-btn.v-btn >>> .v-btn__content {
    white-space: normal;
    max-width: 100%;
    flex: 1 1 auto;
    line-height: 1.25;
  }

  @media (max-width: 599px) {
    .gr-fs { margin-top: 40px; }
    .gr-fs-card { padding: 22px; }
    .gr-fs-titulo { font-size: 1.25rem; }

    .gr-fs-btn.v-btn,
    .gr-fs-wa-btn.v-btn {
      width: 100%;
      height: auto !important;
      min-height: 46px;
      white-space: normal;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
      font-size: 0.98rem;
    }

    .gr-fs-btn.v-btn >>> .v-btn__content,
    .gr-fs-wa-btn.v-btn >>> .v-btn__content {
      justify-content: center;
      text-align: center;
    }
  }
</style>
